import React, { Component } from 'react';

import { bool, number, string } from 'prop-types';

import { TextAreaAPI } from '@philips-emr/tws-components-api';
import { Expression, Size } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { eventListener, oneOf } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { validate } from '@philips-emr/tws-core/lib/services/model-service';
import { MAX_LENGTH } from '@philips-emr/tws-model/lib/validators/max-length';
import { classNames, defaultClasses, isEnumValid, isFunction, isMobile, trigger } from '@philips-emr/tws-utils';

import './text-area.css';

function Counter({
  charactersRemainingExpression = Expression.CHARACTERS_REMAINING,
  length = 0,
  value = '',
}) {
  if (!length) {
    return null;
  }

  if (isMobile()) {
    const count = `(${value.length}/${length})`;
    return <span className="c-text-area__counter">{count}</span>;
  }

  const remaining = length - value.length;
  const text = `${Math.max(0, remaining)} ${msg(charactersRemainingExpression)}`;

  const className = classNames({
    'c-text-area__counter': true,
    'is-invalid': remaining < 0,
  });

  return <span className={className}>{text}</span>;
}

Counter.propTypes = {
  charactersRemainingExpression: string,
  length: number,
  value: string,
};

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: TextAreaAPI })
class TextArea extends Component {
  static propTypes = {
    className: string,
    enabled: bool,
    length: number,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: string,
    readOnly: bool,
    required: bool,
    resize: bool,
    rows: number,
    size: oneOf(Object.values(Size)),
    valid: bool,
    value: string,
  };

  static defaultProps = {
    enabled: true,
    length: 0,
    resize: false,
    rows: 3,
    size: Size.SMALL,
    valid: true,
    value: '',
  };

  getKeyEventObject = (event) => {
    const { value: oldValue } = this.props;
    const { altKey, charCode, ctrlKey, key, keyCode, shiftKey, target } = event;

    const { value } = target;

    return { altKey, charCode, ctrlKey, key, keyCode, oldValue, shiftKey, value };
  };

  onChange = (event) => {
    const { value: oldValue, onChange, length } = this.props;
    const { value } = event.target;

    const validations = validate(value).apply(MAX_LENGTH, { max: length }).run();

    isFunction(onChange) && onChange({ value, oldValue, validations });
  };

  onKeyDown = (event) => {
    const { onKeyDown } = this.props;
    const params = this.getKeyEventObject(event);

    isFunction(onKeyDown) && onKeyDown(params);
  };

  onKeyPress = (event) => {
    const { onKeyPress } = this.props;
    const params = this.getKeyEventObject(event);

    isFunction(onKeyPress) && onKeyPress(params);
  };

  onKeyUp = (event) => {
    const { onKeyUp } = this.props;
    const params = this.getKeyEventObject(event);

    isFunction(onKeyUp) && onKeyUp(params);
  };

  renderFooter = () => {
    const { enabled, length, readOnly, value } = this.props;

    if (!enabled || readOnly) {
      return null;
    }

    return (
      <div className="c-text-area__footer u-hide@view">
        <Counter length={length} value={value} />
      </div>
    );
  };

  render() {
    const { className, enabled, name, onBlur, onFocus, placeholder, readOnly, resize, rows, size, valid, value } = this.props;

    const defClasses = defaultClasses(this.props);

    const classes = classNames({
      'c-text-area': true,
      'c-text-area--sm': isEnumValid(Size, size) && Size.SMALL === size,
      'c-text-area--validated': !valid,
      ...defClasses,
      [className]: Boolean(className),
    });

    const style = {
      resize: resize ? '' : 'none',
    };

    // red lines under the text;
    const autoCorrect = readOnly ? 'off' : 'on';
    const spellCheck = !readOnly;

    return (
      <div className={classes}>
        <textarea
          autoCorrect={autoCorrect}
          className="c-text-area__input"
          disabled={!enabled}
          name={name}
          onBlur={trigger(onBlur)}
          onChange={this.onChange}
          onFocus={trigger(onFocus)}
          onKeyDown={this.onKeyDown}
          onKeyPress={this.onKeyPress}
          onKeyUp={this.onKeyUp}
          placeholder={placeholder}
          readOnly={readOnly}
          rows={rows}
          spellCheck={spellCheck}
          style={style}
          value={value}
        />
        {this.renderFooter()}
      </div>
    );
  }
}

export default TextArea;
